import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

// SupportedLanguage is now a string type for flexibility
export type SupportedLanguage = string;

export interface Translations {
  [key: string]: string;
}

export interface TranslationsFile {
  [languageCode: string]: Translations;
}

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private translations: Translations = {};
  private currentLanguage$ = new BehaviorSubject<SupportedLanguage>('de');
  private translationsLoaded$ = new BehaviorSubject<boolean>(false);
  private translationsCache: Map<SupportedLanguage, Translations> = new Map();
  private supportedLanguages$ = new BehaviorSubject<SupportedLanguage[]>([]);
  private translationsFile$ = new BehaviorSubject<TranslationsFile | null>(null);
  private defaultLanguage: SupportedLanguage = 'de';
  private databaseLanguage: SupportedLanguage | null = null; // Language from database (set later)

  constructor(private http: HttpClient) {
    // Load all translation files first (global + all pages)
    this.loadTranslationsFile().subscribe((mergedFile) => {
      // After all files are loaded and merged, set initial language
      // Now that languages are loaded, validate saved language against actual supported languages
      const savedLanguage = this.getSavedLanguage();
      const finalLanguage = this.validateLanguage(savedLanguage);
      this.currentLanguage$.next(finalLanguage);
      
      // Set HTML lang attribute immediately
      document.documentElement.lang = finalLanguage;
      
      // Set translations from merged file immediately
      if (mergedFile && mergedFile[finalLanguage]) {
        this.translations = mergedFile[finalLanguage];
        this.translationsCache.set(finalLanguage, mergedFile[finalLanguage]);
        this.translationsLoaded$.next(true);
      } else {
        // Fallback: try to load translations
        this.loadTranslations(finalLanguage);
      }
    });
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): SupportedLanguage {
    return this.currentLanguage$.value;
  }

  /**
   * Get current language as Observable
   */
  getCurrentLanguage$(): Observable<SupportedLanguage> {
    return this.currentLanguage$.asObservable();
  }

  /**
   * Check if translations are loaded
   */
  areTranslationsLoaded$(): Observable<boolean> {
    return this.translationsLoaded$.asObservable();
  }

  /**
   * Set language and load translations
   */
  setLanguage(language: SupportedLanguage): void {
    if (this.currentLanguage$.value !== language) {
      this.currentLanguage$.next(language);
      this.saveLanguage(language);
      this.loadTranslations(language);
    }
  }

  /**
   * Get translation for a key
   */
  translate(key: string, params?: { [key: string]: string }): string {
    // First check current translations
    let translation = this.translations[key];
    
    // If not found in current translations, try to get from loaded file
    if (!translation || translation === key) {
      const file = this.translationsFile$.value;
      const currentLang = this.currentLanguage$.value;
      if (file && file[currentLang] && file[currentLang][key]) {
        translation = file[currentLang][key];
        // Update current translations for next time
        this.translations[key] = translation;
      } else {
        // Still not found - return key as fallback
        translation = key;
      }
    }

    // Replace parameters if provided
    if (params && translation && translation !== key) {
      Object.keys(params).forEach(paramKey => {
        translation = translation.replace(`{{${paramKey}}}`, params[paramKey]);
      });
    }

    return translation;
  }

  /**
   * Get translation as Observable (for reactive updates)
   */
  translate$(key: string, params?: { [key: string]: string }): Observable<string> {
    return this.translationsLoaded$.pipe(
      map(loaded => {
        if (!loaded) {
          return key; // Return key if translations not loaded yet
        }
        return this.translate(key, params);
      }),
      shareReplay(1)
    );
  }

  /**
   * Load all translation files and merge them
   * Loads global.json first, then merges with all page-specific files
   */
  private loadTranslationsFile(): Observable<TranslationsFile> {
    const file = this.translationsFile$.value;
    if (file) {
      return of(file);
    }

    // Load global.json first (always required)
    return this.http.get<TranslationsFile>('/assets/translations/global.json')
      .pipe(
        switchMap(globalData => {
          // Discover available languages from global file
          const languages = Object.keys(globalData);
          this.supportedLanguages$.next(languages);
          
          // Set default language
          if (languages.includes(this.defaultLanguage)) {
            // Default language is available
          } else if (languages.length > 0) {
            this.defaultLanguage = languages[0];
          }
          
          // Load all page-specific translation files and merge
          const pageFiles = ['settings', 'invitations', 'invoices', 'errors', 'results', 'tools', 'shop', 'feedback', 'login', 'components'];
          const pageLoads = pageFiles.map(page => 
            this.http.get<TranslationsFile>(`/assets/translations/${page}.json`)
              .pipe(
                catchError(() => of({})) // Ignore errors for missing page files
              )
          );
          
          // Wait for all page files to load, then merge everything
          return pageLoads.length > 0 
            ? combineLatest([of(globalData), ...pageLoads])
            : of([globalData]);
        }),
        map((results: TranslationsFile[]) => {
          // Merge all translation files
          const merged: TranslationsFile = {};
          
          // Get all languages from all files
          const allLanguages = new Set<string>();
          results.forEach(file => {
            if (file) {
              Object.keys(file).forEach(lang => allLanguages.add(lang));
            }
          });
          
          // Merge translations for each language
          allLanguages.forEach(lang => {
            merged[lang] = {};
            // Merge all files for this language (order matters - later files override earlier ones)
            results.forEach(file => {
              if (file && file[lang]) {
                merged[lang] = { ...merged[lang], ...file[lang] };
              }
            });
          });
          
          this.translationsFile$.next(merged);
          return merged;
        }),
        catchError(error => {
          console.error('Error loading translations files:', error);
          // Fallback to default languages
          this.supportedLanguages$.next(['de', 'en', 'tr']);
          return of({});
        })
      );
  }

  /**
   * Load page-specific translation file and merge with existing translations
   */
  private loadPageTranslationsFile(page: string): Observable<TranslationsFile> {
    return this.http.get<TranslationsFile>(`/assets/translations/${page}.json`)
      .pipe(
        map(pageData => {
          const current = this.translationsFile$.value || {};
          const merged: TranslationsFile = {};
          
          // Merge translations for each language
          const languages = Object.keys(current).length > 0 ? Object.keys(current) : Object.keys(pageData);
          languages.forEach(lang => {
            merged[lang] = {
              ...(current[lang] || {}),
              ...(pageData[lang] || {})
            };
          });
          
          this.translationsFile$.next(merged);
          return merged;
        }),
        catchError(error => {
          console.warn(`Error loading page translations for ${page}:`, error);
          // Return current translations if page file doesn't exist
          return of(this.translationsFile$.value || {});
        })
      );
  }

  /**
   * Load translations for a specific language
   * Optionally loads page-specific translations
   */
  private loadTranslations(language: SupportedLanguage, page?: string): void {
    // Check cache first
    const cacheKey = page ? `${language}:${page}` : language;
    if (this.translationsCache.has(cacheKey)) {
      this.translations = this.translationsCache.get(cacheKey)!;
      // Update HTML lang attribute
      document.documentElement.lang = language;
      this.translationsLoaded$.next(true);
      return;
    }

    // Get from already loaded file
    const file = this.translationsFile$.value;
    if (file && file[language]) {
      this.translations = file[language];
      this.translationsCache.set(cacheKey, file[language]);
      // Update HTML lang attribute
      document.documentElement.lang = language;
      this.translationsLoaded$.next(true);
      return;
    }

    // Load global file first, then page-specific if provided
    let loadObservable = this.loadTranslationsFile();
    
    if (page) {
      // Chain page-specific loading
      loadObservable = loadObservable.pipe(
        // Switch to page loading and merge
        switchMap(() => this.loadPageTranslationsFile(page))
      );
    }
    
    loadObservable.pipe(
      map(() => {
        const finalFile = this.translationsFile$.value;
        const langTranslations = finalFile?.[language] || {};
        // Ensure translations are set
        this.translations = langTranslations;
        // Update HTML lang attribute
        document.documentElement.lang = language;
        return langTranslations;
      }),
      catchError(error => {
        console.error('Error loading translations:', error);
        return of({});
      })
    )
    .subscribe(translations => {
      this.translations = translations;
      this.translationsCache.set(cacheKey, translations);
      // Ensure lang attribute is set even if translations are empty
      document.documentElement.lang = language;
      this.translationsLoaded$.next(true);
    });
  }

  /**
   * Load translations for a specific page
   * This merges page-specific translations with global translations
   */
  loadPageTranslations(page: string, language?: SupportedLanguage): void {
    const lang = language || this.currentLanguage$.value;
    this.loadTranslations(lang, page);
  }

  /**
   * Get saved language with priority: Database > localStorage > Browser > Default
   * Validates against dynamically discovered supported languages
   */
  private getSavedLanguage(): SupportedLanguage {
    const supported = this.supportedLanguages$.value;
    
    // Priority 1: Language from database (if set)
    if (this.databaseLanguage) {
      if (supported.length > 0 && supported.includes(this.databaseLanguage)) {
        return this.databaseLanguage;
      }
      // If database language is not supported, validate format and continue to next priority
      if (this.isValidLanguageCode(this.databaseLanguage)) {
        // Will be validated again after languages are loaded
        return this.databaseLanguage;
      }
    }
    
    // Priority 2: Language from localStorage
    try {
      const saved = localStorage.getItem('app_language');
      if (saved) {
        // Check if saved language is in supported languages (dynamically discovered)
        if (supported.length > 0 && supported.includes(saved)) {
          return saved;
        }
        // If languages not loaded yet, validate that saved value is a valid language code format
        if (this.isValidLanguageCode(saved)) {
          // Return saved value, but it will be validated again after languages are loaded
          return saved;
        }
      }
    } catch (error) {
      console.error('Error reading language from localStorage:', error);
    }

    // Priority 3: Detect browser language as default
    const browserLang = this.detectBrowserLanguage();
    if (browserLang) {
      return browserLang;
    }

    // Priority 4: Default to configured default language or first available
    return supported.length > 0 ? supported[0] : this.defaultLanguage;
  }

  /**
   * Detect browser language and match against supported languages
   * Returns matched language or null if no match found
   */
  private detectBrowserLanguage(): SupportedLanguage | null {
    if (typeof navigator === 'undefined' || !navigator.language) {
      return null;
    }

    const browserLang = navigator.language.toLowerCase();
    const supported = this.supportedLanguages$.value;
    
    // Try exact match first (e.g., 'de' matches 'de')
    if (supported.length > 0 && supported.includes(browserLang)) {
      return browserLang;
    }
    
    // Try language prefix match (e.g., 'de-DE' -> 'de', 'en-US' -> 'en')
    for (const lang of supported) {
      const langPrefix = lang.split('-')[0];
      if (browserLang.startsWith(langPrefix)) {
        return lang;
      }
    }

    // Try navigator.languages array (preferred languages in order)
    if (navigator.languages && Array.isArray(navigator.languages)) {
      for (const preferredLang of navigator.languages) {
        const langLower = preferredLang.toLowerCase();
        // Try exact match
        if (supported.length > 0 && supported.includes(langLower)) {
          return langLower;
        }
        // Try prefix match
        for (const lang of supported) {
          const langPrefix = lang.split('-')[0];
          if (langLower.startsWith(langPrefix)) {
            return lang;
          }
        }
      }
    }

    return null;
  }

  /**
   * Set language from database
   * This should be called when user data is loaded from the database
   * @param language - Language code from database
   */
  setLanguageFromDatabase(language: SupportedLanguage | null): void {
    this.databaseLanguage = language;
    // If database language is set and different from current, update language
    if (language && language !== this.currentLanguage$.value) {
      const validatedLanguage = this.validateLanguage(language);
      if (validatedLanguage !== this.currentLanguage$.value) {
        this.setLanguage(validatedLanguage);
      }
    }
  }

  /**
   * Validate if a string is a valid language code format
   * Accepts ISO 639-1 codes (2 letters) or ISO 639-1 + region (e.g., 'en-US')
   * This allows any language code to be saved without hardcoding
   */
  private isValidLanguageCode(code: string): boolean {
    // Basic validation: 2-5 characters, lowercase letters and hyphens only
    // Matches patterns like: 'de', 'en', 'en-us', 'da', 'tr', etc.
    return /^[a-z]{2}(-[a-z]{2,3})?$/.test(code);
  }

  /**
   * Validate language against supported languages
   * If language is not supported, falls back to default or first available
   */
  private validateLanguage(language: SupportedLanguage): SupportedLanguage {
    const supported = this.supportedLanguages$.value;
    
    // If languages are loaded and language is supported, use it
    if (supported.length > 0 && supported.includes(language)) {
      return language;
    }
    
    // If not supported but languages are loaded, use default or first available
    if (supported.length > 0) {
      // Try default language first
      if (supported.includes(this.defaultLanguage)) {
        return this.defaultLanguage;
      }
      // Otherwise use first available
      return supported[0];
    }
    
    // If languages not loaded yet, return as-is (will be validated later)
    return language;
  }

  /**
   * Save language to localStorage
   */
  private saveLanguage(language: SupportedLanguage): void {
    try {
      localStorage.setItem('app_language', language);
      // Update HTML lang attribute
      document.documentElement.lang = language;
    } catch (error) {
      console.error('Error saving language to localStorage:', error);
    }
  }

  /**
   * Get all supported languages
   */
  getSupportedLanguages(): SupportedLanguage[] {
    return this.supportedLanguages$.value;
  }

  /**
   * Get all supported languages as Observable
   */
  getSupportedLanguages$(): Observable<SupportedLanguage[]> {
    return this.supportedLanguages$.asObservable();
  }

  /**
   * Get language display name
   * Tries to get from translations (language.{code}), falls back to language code
   */
  getLanguageDisplayName(language: SupportedLanguage): string {
    // Try to get display name from translations
    const translationKey = `language.${language}`;
    const displayName = this.translate(translationKey);
    
    // If translation exists and is different from key, use it
    if (displayName && displayName !== translationKey) {
      return displayName;
    }
    
    // Fallback: try to get from loaded translations file
    const file = this.translationsFile$.value;
    if (file && file[language]) {
      const langDisplayName = file[language][translationKey];
      if (langDisplayName) {
        return langDisplayName;
      }
    }
    
    // Final fallback: format language code nicely
    return this.formatLanguageCode(language);
  }

  /**
   * Format language code for display (e.g., 'en' -> 'English')
   */
  private formatLanguageCode(code: string): string {
    const parts = code.split('-');
    const lang = parts[0].toUpperCase();
    const region = parts[1] ? ` (${parts[1].toUpperCase()})` : '';
    return lang + region;
  }

  /**
   * Check if a language is supported
   */
  isLanguageSupported(language: string): boolean {
    return this.supportedLanguages$.value.includes(language);
  }
}
